import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Form, Modal, Popover, Select, Tag } from "antd";
import { useCallback, useMemo, useRef, useState } from "react";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";
import { FORM_VALUE_DIFFER_FROM_PRESET_NOTICE_PSEUDO_ELEMENT_CLASS_NAME } from "./valueDifferFromTemplateNotice.js";
import { useFormContentBlocker } from "../../contexts/formContentBlocker.js";
import { useFormService } from "../../contexts/formService.js";
import { useI18n } from "../../contexts/i18n.js";
import { formContentBlockerAttributesFromTemplate } from "../../hooks/useFormContentBlockerHandler.js";
import { formServiceAttributesFromTemplate } from "../../hooks/useFormServiceHandler.js";
const FORM_VALUE_DIFFER_FROM_PRESET_TAG_APPLY_EVENT = "RCB/FormValueDifferFromTemplateTag/Apply";
const replacer = (key, value)=>value instanceof Object && !(value instanceof Array) ? Object.keys(value).sort().reduce((sorted, key)=>{
        sorted[key] = value[key];
        return sorted;
    }, {}) : value;
const diffByJsonStringify = (value, difference)=>JSON.stringify(value, replacer) !== JSON.stringify(difference, replacer);
function FormValueDifferFromTemplateContent({ form, difference, newValueText, renderDiff, valueName, currentValue, splitView, useModal }) {
    const { __ } = useI18n();
    const { template } = form === "service" ? useFormService() : useFormContentBlocker();
    const groups = form === "service" ? useFormService().groups || [] : [];
    const showDiff = typeof renderDiff !== "function";
    const [selectedVersionNumber, setSelectedVersionNumber] = useState(template.version);
    const { lang } = document.documentElement;
    const versionValues = useMemo(()=>[
            template,
            ...template.consumerData.versions
        ].map((t)=>({
                ...form === "service" ? formServiceAttributesFromTemplate(t, {
                    groups
                }) : formContentBlockerAttributesFromTemplate(t),
                version: t.version,
                versionOutput: new Date(t.createdAt).toLocaleDateString(lang)
            })), [
        template,
        groups
    ]);
    const selectOptions = useMemo(()=>{
        const findVersionWithNumber = (search)=>versionValues.filter(({ version })=>version === search)[0];
        return versionValues.map(({ version, versionOutput }, i)=>{
            let leftVersion = "currentValue";
            let rightVersion = findVersionWithNumber(version);
            let label = version === template.version ? __("Version %s (latest)", versionOutput) : __("Version %s", versionOutput);
            if (showDiff) {
                if (i === 0) {
                    label = `${__("Your value")} → ${label}`;
                } else {
                    const { version: nextVersion, versionOutput: nextVersionOutput } = versionValues[i - 1];
                    label = nextVersion === template.version ? `${label} → ${__("Version %s (latest)", nextVersionOutput)}` : `${label} → ${__("Version %s", nextVersionOutput)}`;
                    leftVersion = findVersionWithNumber(version);
                    rightVersion = findVersionWithNumber(nextVersion);
                }
            }
            return {
                version,
                label,
                leftVersion,
                rightVersion
            };
        });
    }, [
        template.version,
        versionValues,
        showDiff,
        __
    ]);
    const selectedOption = useMemo(()=>selectOptions.filter(({ version })=>version === selectedVersionNumber)[0], [
        selectOptions,
        selectedVersionNumber
    ]);
    const [oldValue, newValue] = useMemo(()=>{
        const { leftVersion, rightVersion } = selectedOption;
        return [
            leftVersion === "currentValue" ? currentValue : leftVersion[valueName],
            rightVersion[valueName]
        ];
    }, [
        currentValue,
        versionValues,
        selectedOption
    ]);
    const diff = difference(oldValue, newValue);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Select, {
                style: {
                    float: "right",
                    width: 300,
                    marginRight: useModal ? 15 : 0
                },
                value: selectedVersionNumber,
                onChange: setSelectedVersionNumber,
                size: "small",
                children: [
                    selectOptions.map(({ version, label })=>/*#__PURE__*/ _jsx(Select.Option, {
                            value: version,
                            children: label
                        }, version)),
                    selectOptions[selectOptions.length - 1].version !== 1 && /*#__PURE__*/ _jsx(Select.Option, {
                        value: 0,
                        disabled: true,
                        children: /*#__PURE__*/ _jsx("i", {
                            children: __("Older versions not available")
                        })
                    }, 0)
                ]
            }),
            diff === undefined ? __("The version you selected does not show any difference to your current form value.") : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "description",
                        style: {
                            marginBottom: 10
                        },
                        children: __("The value you entered is different from the value in the template.")
                    }),
                    newValueText !== false && /*#__PURE__*/ _jsx("p", {
                        children: /*#__PURE__*/ _jsx("strong", {
                            children: newValueText || __("Default value:")
                        })
                    }),
                    !showDiff ? renderDiff(newValue, diff) : /*#__PURE__*/ _jsx("div", {
                        style: {
                            maxHeight: 200,
                            overflow: "auto"
                        },
                        children: /*#__PURE__*/ _jsx(ReactDiffViewer, {
                            styles: {
                                splitView: {
                                    fontSize: 11
                                },
                                contentText: {
                                    lineHeight: "16px !important"
                                },
                                wordDiff: {
                                    wordBreak: "break-word !important",
                                    padding: 0
                                },
                                gutter: {
                                    minWidth: "auto !important"
                                },
                                marker: {
                                    paddingLeft: "3px !important",
                                    paddingRight: "3px !important"
                                }
                            },
                            oldValue: oldValue,
                            newValue: newValue,
                            compareMethod: DiffMethod.WORDS,
                            splitView: typeof splitView === "boolean" ? splitView : true,
                            showDiffOnly: false
                        })
                    })
                ]
            })
        ]
    });
}
function FormValueDifferFromTemplateTag(props) {
    const { useModal, form, valueName, difference, apply, widthOfRef, placement = "bottomLeft", noBr = false, className, style, ignoreEmptyDefaultValue, popoverProps } = props;
    const { __ } = useI18n();
    const { template, defaultTemplateValues: { [valueName]: defaultValue } } = form === "service" ? useFormService() : useFormContentBlocker();
    const [isModalVisible, setIsModalVisible] = useState(false);
    // Memoize width as it could be `undefined` due to UI lacks
    const widthRef = useRef();
    const calculateDifference = useCallback((value, defaultValue)=>{
        return difference ? difference(value, defaultValue) : value !== defaultValue ? true : undefined;
    }, [
        difference
    ]);
    if (ignoreEmptyDefaultValue && typeof defaultValue === "string" && defaultValue === "" || [
        "undefined",
        "null",
        "[]",
        "{}"
    ].indexOf(JSON.stringify(defaultValue)) > -1 || typeof defaultValue === "object" && Object.values(defaultValue).filter((v)=>v !== "").length === 0) {
        return null;
    }
    return typeof defaultValue !== "undefined" && template && /*#__PURE__*/ _jsx(Form.Item, {
        noStyle: true,
        shouldUpdate: (prevValues, nextValues)=>{
            return prevValues[valueName] !== nextValues[valueName] || widthRef.current !== widthOfRef?.clientWidth;
        },
        children: ({ getFieldValue, setFieldsValue })=>{
            const value = getFieldValue(valueName);
            const diff = calculateDifference(value, defaultValue);
            if (!diff) {
                return null;
            }
            widthRef.current = widthOfRef?.clientWidth || widthRef.current;
            const handleApply = ()=>{
                if (typeof apply === "function") {
                    apply(diff, setFieldsValue, value);
                } else {
                    setFieldsValue({
                        [valueName]: defaultValue
                    });
                }
                document.dispatchEvent(new CustomEvent(FORM_VALUE_DIFFER_FROM_PRESET_TAG_APPLY_EVENT));
                setIsModalVisible(false);
            };
            const content = /*#__PURE__*/ _jsx(FormValueDifferFromTemplateContent, {
                ...props,
                currentValue: value,
                difference: calculateDifference
            });
            const tagComponent = /*#__PURE__*/ _jsxs(Tag, {
                color: "blue",
                style: {
                    cursor: "pointer",
                    ...style
                },
                className: className,
                onClick: ()=>useModal && setIsModalVisible(true),
                children: [
                    __("Differing from template"),
                    /*#__PURE__*/ _jsx("span", {
                        className: FORM_VALUE_DIFFER_FROM_PRESET_NOTICE_PSEUDO_ELEMENT_CLASS_NAME,
                        style: {
                            display: "none"
                        },
                        onClick: (e)=>{
                            handleApply();
                            // Do not trigger the `<Tag onClick` event
                            e.stopPropagation();
                        }
                    })
                ]
            });
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    useModal ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Modal, {
                                open: isModalVisible,
                                onOk: handleApply,
                                onCancel: ()=>setIsModalVisible(false),
                                okText: __("Use default value"),
                                cancelText: __("Cancel"),
                                width: "calc(100% - 50px)",
                                children: content
                            }),
                            tagComponent
                        ]
                    }) : /*#__PURE__*/ _jsx(Popover, {
                        destroyTooltipOnHide: true,
                        overlayStyle: {
                            width: widthRef.current
                        },
                        content: /*#__PURE__*/ _jsxs("div", {
                            className: "wp-clearfix",
                            children: [
                                content,
                                /*#__PURE__*/ _jsx("a", {
                                    className: "button button-primary alignright",
                                    onClick: handleApply,
                                    style: {
                                        marginTop: 10
                                    },
                                    children: __("Use default value")
                                })
                            ]
                        }),
                        title: undefined,
                        placement: placement,
                        ...popoverProps,
                        children: tagComponent
                    }),
                    !noBr && /*#__PURE__*/ _jsx("br", {})
                ]
            });
        }
    });
}
export { diffByJsonStringify, FormValueDifferFromTemplateTag, FORM_VALUE_DIFFER_FROM_PRESET_TAG_APPLY_EVENT };
